import sha3 from 'crypto-js/sha3';
import Base64 from 'crypto-js/enc-base64';
import chunk from 'lodash/chunk';
import difference from 'lodash/difference';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import keyBy from 'lodash/keyBy';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import config from '../config';
import {
  EMPTY_ARRAY,
  BEGIN_SEARCH_REQUEST,
  DEFAULT_SEARCH_SIZE,
  DEFAULT_ITERATION_COUNT,
  END_SEARCH_REQUEST,
  INDEX_BY_CLIP_SLUG,
  INDEX_BY_SCENE_ID,
  INDEX_BY_SCENE_SLUG,
  LOAD_CLIPS,
  QUERY_PARSER,
  SEARCH_SOURCE,
  SET_SEARCH_SOURCE,
  SET_SEARCH_TEXT,
  SORT_DIRECTION,
  TOGGLE_FILTER_FAVORITES,
  TOGGLE_FILTER_LICENSED,
  TOGGLE_SEARCH_TAG,
  UPDATE_SEARCH_REQUEST,
} from '../constants';
import { clipsIdsToX } from '../lib/clips';
import { getTag, groupTags } from '../lib/tags';
import { buildAction } from './helpers';

const MAX_CLIPID_SEARCH = 50;

export function setSearchText(text) {
  return buildAction(SET_SEARCH_TEXT, text);
}

export function setSearchSource(source) {
  return buildAction(SET_SEARCH_SOURCE, source);
}

export function toggleTag(tagId, state) {
  return buildAction(TOGGLE_SEARCH_TAG, { tagId: tagId, state: state });
}

export function clearTags() {
  return function(dispatch, getState) {
    getState().search.tags.map(tagId => dispatch(toggleTag(tagId, false)));
  };
}

export function toggleFilterFavorites(state) {
  return buildAction(TOGGLE_FILTER_FAVORITES, state);
}

export function toggleFilterLicensed(state) {
  return buildAction(TOGGLE_FILTER_LICENSED, state);
}

export function getLibraryClipCount() {
  return async function(dispatch) {
    let results = await dispatch(search(buildSearchParams()));
    return results.found;
  };
}

export function getLatestScenes(min = 5, max = 10) {
  return async function(dispatch) {
    let lastLength = 0;
    let more = false;
    let clips;
    let sceneIds;

    while (true) {
      let func = more ? searchNext : search;
      let results = await dispatch(func(buildSearchParams()));

      clips = results.clips;
      if (results.nextClips) {
        clips = clips.concat(results.nextClips);
      }

      sceneIds = uniq(clips.map(clip => clip.scene_id));

      if (sceneIds.length === lastLength) {
        break;
      }
      lastLength = sceneIds.length;

      if (sceneIds.length >= min) {
        break;
      }

      more = true;
    }

    sceneIds = sceneIds.slice(0, max);

    let scenePromises = sceneIds.map(sceneId =>
      dispatch(clipsFromSceneId(sceneId)),
    );

    let scenes = await Promise.all(scenePromises);

    scenes = sortBy(
      scenes,
      sceneClips => get(sceneClips, '[0].published_at', '0'),
      // timestamp is a string '2017-12-19T19:59:23.051Z' so compare to a string: '0'.
    );
    scenes.reverse();

    return scenes;
  };
}

export function buildSearchParams(
  text = '',
  tags = EMPTY_ARRAY,
  source = SEARCH_SOURCE.ALL_SCENES,
  state = {},
  filterToFavorites = false,
  filterToLicensed = false,
) {
  const tagQuery = buildTagQuery(tags);
  const textQuery = text
    ? `'${text.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
    : '';
  const query = [textQuery, tagQuery];
  let filter = [];
  const textAndTags = groupTerms('and', query);

  // Source impacts the fallback query and sorting.
  let searchSource = source;
  if (filterToFavorites || filterToLicensed) {
    searchSource = SEARCH_SOURCE.ALL_SCENES;

    const favoriteSceneIds = uniq(
      clipsIdsToX(
        values(state.clips.clips),
        state.user.favorites.map(fav => fav.clipId),
        'scene_id',
      ),
    );
    const licensedSceneIds = uniq(
      clipsIdsToX(
        values(state.clips.clips),
        state.user.licenses.map(license => license.clipId),
        'scene_id',
      ),
    );
    let filterToSceneIds = favoriteSceneIds.concat(licensedSceneIds);
    if (filterToFavorites && filterToLicensed) {
      filterToSceneIds = intersection(favoriteSceneIds, licensedSceneIds);
    }
    if (filterToSceneIds.length < 1) {
      filterToSceneIds = ['matchnone'];
    }

    filter.push(
      groupTerms(
        'or',
        filterToSceneIds.map(sceneId => `scene_id:'${sceneId}'`),
      ),
    );
  }

  let sort; // Sort by score (natural sort).
  switch (searchSource) {
    case SEARCH_SOURCE.ALL_SCENES:
      if (!textAndTags) {
        query.push('matchall');
      }
      sort = `_score ${SORT_DIRECTION.DESC}, published_at ${SORT_DIRECTION.DESC}`;
      break;
    case SEARCH_SOURCE.FEATURED:
      // Featured_at is anything in this millennium. :P
      query.push("featured_at:['2000-01-01T00:00:00Z','3000-01-01T00:00:00Z']");
      sort = `featured_at ${SORT_DIRECTION.DESC}`;
      break;
  }

  return {
    query: groupTerms('and', query),
    filter: groupTerms('and', filter),
    sorting: sort,
  };
}

export function buildGlobalSearchParams(state) {
  return buildSearchParams(
    state.search.text,
    state.search.tags,
    state.search.source,
    state,
    state.search.filterToFavorites,
    state.search.filterToLicensed,
  );
}

export function clipsFromClipIds(clipIds) {
  return async function(dispatch, getState) {
    const beforeState = getState();
    const clipIdsToLoad = difference(
      clipIds,
      Object.keys(beforeState.clips.clips),
    );

    if (clipIdsToLoad.length) {
      const loadChunks = chunk(clipIdsToLoad, MAX_CLIPID_SEARCH);
      for (let loadChunk of loadChunks) {
        const query = `(or ${loadChunk
          .map(clipId => `clip_id:'${clipId}'`)
          .join(' ')})`;
        await dispatch(searchNext({ query: query }, { iterations: -1 }));
      }
    }

    const afterState = getState();
    return clipIds.map(clipId => afterState.clips.clips[clipId]);
  };
}

export function clipsFromClipSlug(clipSlug) {
  return async function(dispatch, getState) {
    const beforeState = getState();
    const beforeClipIds = get(beforeState, [
      'clips',
      'indexByClipSlug',
      clipSlug,
    ]);
    if (beforeClipIds !== undefined) {
      return beforeClipIds.map(id => beforeState.clips.clips[id]);
    }

    const results = await dispatch(
      searchNext({
        query: `clip_slug:'${clipSlug
          .replace(/\\/g, '\\\\')
          .replace(/'/g, "\\'")}'`,
      }),
    );
    let allClips = results.clips.concat(results.nextClips);

    const clipIds = allClips.map(clip => clip.clip_id);
    dispatch(
      buildAction(INDEX_BY_CLIP_SLUG, { key: clipSlug, value: clipIds }),
    );

    return allClips;
  };
}

export function clipsFromSceneId(sceneId) {
  return async function(dispatch, getState) {
    const beforeState = getState();

    // Try to take the shortest path to get the clips. The indexBySceneId.
    const beforeClipIds = get(beforeState, [
      'clips',
      'indexBySceneId',
      sceneId,
    ]);
    if (beforeClipIds !== undefined) {
      return beforeClipIds.map(clipId => beforeState.clips.clips[clipId]);
    }

    // If that fails we can still run through the whole list of clips and see if all the clips for a
    // given id are loaded. This should still be faster than making a network request and then doing
    // all the parsing and organizing of data we might already have.
    let alreadyLoaded = checkLoadedClipsForScene(
      dispatch,
      beforeState.clips.clips,
      'scene_id',
      sceneId,
    );
    if (alreadyLoaded !== undefined) {
      return alreadyLoaded;
    }

    const results = await dispatch(
      searchNext({ query: `scene_id:'${sceneId}'` }, { iterations: -1 }),
    );
    const allClips = results.clips.concat(results.nextClips);

    const clipIds = allClips.map(clip => clip.clip_id);
    dispatch(buildAction(INDEX_BY_SCENE_ID, { key: sceneId, value: clipIds }));
    const sceneSlug = get(allClips[0], 'scene_slug');
    if (sceneSlug !== undefined) {
      dispatch(
        buildAction(INDEX_BY_SCENE_SLUG, { key: sceneSlug, value: clipIds }),
      );
    }

    return allClips;
  };
}

export function clipsFromSceneSlug(sceneSlug) {
  return async function(dispatch, getState) {
    const beforeState = getState();

    // Try to take the shortest path to get the clips. The indexBySceneSlug.
    const beforeClipIds = get(beforeState, [
      'clips',
      'indexBySceneSlug',
      sceneSlug,
    ]);
    if (beforeClipIds !== undefined) {
      return beforeClipIds.map(clipId => beforeState.clips.clips[clipId]);
    }

    // If that fails we can still run through the whole list of clips and see if all the clips for a
    // given slug are loaded. This should still be faster than making a network request and then
    // doing all the parsing and organizing of data we might already have.
    let alreadyLoaded = checkLoadedClipsForScene(
      dispatch,
      beforeState.clips.clips,
      'scene_slug',
      sceneSlug,
    );
    if (alreadyLoaded !== undefined) {
      return alreadyLoaded;
    }

    const results = await dispatch(
      searchNext(
        {
          query: `scene_slug:'${sceneSlug
            .replace(/\\/g, '\\\\')
            .replace(/'/g, "\\'")}'`,
        },
        { iterations: -1 },
      ),
    );
    if (results.clips === undefined) {
      throw new Error(JSON.stringify(results, null, 2));
    }
    const allClips = results.clips.concat(results.nextClips);

    const clipIds = allClips.map(clip => clip.clip_id);
    dispatch(
      buildAction(INDEX_BY_SCENE_SLUG, { key: sceneSlug, value: clipIds }),
    );
    const sceneId = get(allClips[0], 'scene_id');
    if (sceneId) {
      dispatch(
        buildAction(INDEX_BY_SCENE_ID, { key: sceneId, value: clipIds }),
      );
    }

    return allClips;
  };
}

export function search(params) {
  return async function(dispatch, getState) {
    // Get the cached results if there are any.
    let results = await dispatch(searchNext(params, { iterations: 0 }));

    // If there are no results, do an actual search.
    if (results.found === undefined) {
      results = await dispatch(searchNext(params));
    }

    // Break down the results.
    const { clips, nextClips, ...others } = results;

    return {
      clips: clips.concat(nextClips),
      ...others,
    };
  };
}

export function searchNext(params, options) {
  return function(dispatch, getState) {
    const mergedOptions = {
      iterations: DEFAULT_ITERATION_COUNT,
      ...options,
    };
    const { query, filter, sorting, size } = params;
    const sizeParam = size !== undefined ? size : DEFAULT_SEARCH_SIZE;

    const key = queryHash({ query, filter, sorting });
    const beforeState = getState();
    let cachedQuery = get(beforeState, ['search', 'queries', key], {
      hit: EMPTY_ARRAY,
    });
    let cachedClips = EMPTY_ARRAY;

    // Already found some, maybe we're done?
    if (cachedQuery.found !== undefined) {
      // If we've found some, there will be at minimum an empty list here.
      cachedClips = cachedQuery.hit.map(
        clipId => beforeState.clips.clips[clipId],
      );
    }

    // Possible reasons to exit before doing any more searching.
    //  All clips found.
    //  We're told not to do any iterations.
    //  Size of search is less than 1 so even if we tried to search nothing would come back.
    if (
      cachedQuery.found <= cachedClips.length ||
      mergedOptions.iterations === 0 ||
      sizeParam < 1
    ) {
      return Promise.resolve({
        clips: cachedClips,
        nextClips: EMPTY_ARRAY,
        found: cachedQuery.found,
        loaded: cachedClips.length,
        done: cachedQuery.found <= cachedClips.length,
      });
    }

    // Already running? Tag along. The options might be different (iterations and size) but this is
    //  better than just throwing an error. You'll get mostly what you're looking for.
    if (
      cachedQuery.fetching !== undefined &&
      cachedQuery.fetching.then !== undefined
    ) {
      return cachedQuery.fetching;
    }

    let trigger;
    // Construct a new promise so we can save it to the app state and use it again if needed.
    const searchPromise = new Promise(function(resolve, reject) {
      trigger = async function() {
        try {
          let found = cachedQuery.found;
          let iterationsCompleted = 0;
          let nextClips = EMPTY_ARRAY;
          let nextCursor = cachedQuery.cursor;

          while (true) {
            const iterationResults = await dispatch(
              runSearch({
                query: query,
                filter: filter,
                sorting: sorting,
                size: sizeParam,
                cursor: nextCursor,
              }),
            );
            iterationsCompleted += 1;
            nextClips = nextClips.concat(iterationResults.clips);
            found = iterationResults.found;
            nextCursor = iterationResults.cursor;
            const loaded = cachedClips.length + nextClips.length;

            let beDone = false;
            // Out of iterations...
            if (
              mergedOptions.iterations > -1 &&
              iterationsCompleted >= mergedOptions.iterations
            ) {
              beDone = true;
            }
            // All clips loaded?
            if (loaded >= found) {
              beDone = true;
            }
            // Didn't find any more?
            if (iterationResults.clips.length < 1) {
              beDone = true;
            }

            dispatch(
              buildAction(UPDATE_SEARCH_REQUEST, {
                key: key,
                found: found,
                cursor: nextCursor,
                hit: iterationResults.clipIds,
              }),
            );

            if (beDone) {
              resolve({
                clips: cachedClips,
                nextClips: nextClips,
                found: found,
                loaded: loaded,
                done: loaded >= found,
              });
              return;
            }
          }
        } catch (err) {
          reject(err);
        } finally {
          // No matter what, close the loop on using this query.
          dispatch(buildAction(END_SEARCH_REQUEST, { key: key }));
        }
      };
    });

    // Note that we're using this query. There is a race condition where the
    dispatch(
      buildAction(BEGIN_SEARCH_REQUEST, { key: key, fetching: searchPromise }),
    );
    // Fix the race condition where the search starts before we have noted that the search has begun.
    trigger();

    return searchPromise;
  };
}

// Private functions below here.

function queryHash(params) {
  return Base64.stringify(sha3(JSON.stringify(params)));
}

function runSearch(params) {
  return async function(dispatch, getState, { fetch }) {
    // Run our search, conver to json, check for errors.
    const results = await sendSearchToServer(params, fetch)
      .then(results => results.json())
      .then(results => checkForError(params, results));

    // Save the clips in the app state.
    dispatch(buildAction(LOAD_CLIPS, results.hits.hit));

    // Return the clips in the order we found them.
    const state = getState();
    const clipIds = results.hits.hit.map(hit => hit.fields.clip_id);
    const clips = clipIds.map(clipId => state.clips.clips[clipId]);
    return {
      found: results.hits.found,
      clipIds: clipIds,
      clips: clips,
      cursor: results.hits.cursor,
    };
  };
}

function sendSearchToServer(params, fetch) {
  const queryParams = {
    q: params.query,
    'q.parser': QUERY_PARSER.STRUCTURED,
    'q.options': '{"defaultOperator":"and"}',
    sort: params.sorting || '_score desc',
    cursor: params.cursor || 'initial',
    partial: 'true',
    size: params.size || DEFAULT_SEARCH_SIZE,
    return: '_all_fields,_score',
  };
  if (params.filter !== undefined) {
    queryParams.fq = params.filter;
  }

  const queryString = Object.keys(queryParams)
    .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
    .join('&');

  const url = `${config.search.endpoint}?${queryString}`;

  return fetch(url, {
    method: 'GET',
    mode: 'cors',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });
}

function checkForError(params, results) {
  if (results.message) {
    console.error(params);
    throw new Error(results.message);
  }

  if (results.hits === undefined) {
    console.error(results);
    throw new Error('Did not get a hits object back from cloudsearch.');
  }

  return results;
}

function buildTagQuery(tagIds) {
  if (!tagIds.length) {
    return;
  }

  const defaultOp = 'or';
  const ops = {
    Category: {
      undefined: 'and',
    },
  };

  // Sort everything. The results of this are used in a hash based on string conversion so keep it as uniform as possible.

  const tags = tagIds.map(getTag);
  const grouped = groupTags(tags);
  let terms = [];
  sortBy(Object.keys(grouped)).map(typeKey => {
    const type = grouped[typeKey];
    sortBy(Object.keys(type)).map(groupKey => {
      const group = type[groupKey];
      const queryItems = sortBy(group).map(tag => `tags:'${tag.id}'`);
      terms.push(
        groupTerms(get(ops, [typeKey, groupKey], defaultOp), queryItems),
      );
    });
  });

  return groupTerms('and', sortBy(terms));
}

function groupTerms(op, terms) {
  const filteredTerms = sortBy(terms.filter(Boolean));
  switch (filteredTerms.length) {
    case 0:
      return;
    case 1:
      return filteredTerms[0];
    default:
      return `(${op} ${filteredTerms.join(' ')})`;
  }
}

function checkLoadedClipsForScene(dispatch, clips, attribute, value) {
  let sceneClips = [];
  let sceneClipCount;

  Object.keys(clips).map(clipId => {
    let clip = clips[clipId];
    if (clip[attribute] === value) {
      sceneClips.push(clip);
      sceneClipCount = clip.scene_clip_count;
    }
  });

  if (sceneClips.length === sceneClipCount) {
    let anyClip = sceneClips[0];
    let clipIds = sceneClips.map(clip => clip.clip_id);
    dispatch(
      buildAction(INDEX_BY_SCENE_ID, {
        key: anyClip.scene_id,
        value: clipIds,
      }),
    );
    dispatch(
      buildAction(INDEX_BY_SCENE_SLUG, {
        key: anyClip.scene_slug,
        value: clipIds,
      }),
    );

    return sceneClips;
  }
}



// WEBPACK FOOTER //
// ./src/actions/search.js